import { useEffect, useRef, useState } from "react";
import { fetchTmyEstimate, fetchTmyEstimationJob } from "../../services/api";

// Both estimation methods use the same durable job; remounting restores it.
export default function useTmyEstimationJob({ enabled, projectId, accessToken, onComplete, onError }) {
  const [job, setJob] = useState(null);
  const [connectionError, setConnectionError] = useState(false);
  const [revision, setRevision] = useState(0);
  const [progressScope, setProgressScope] = useState(null);
  const callbacks = useRef({ onComplete, onError });
  callbacks.current = { onComplete, onError };
  const scope = `${projectId}:${accessToken}`;
  const [jobScope, setJobScope] = useState(null);
  useEffect(() => {
    if (!enabled || !projectId) { setJob(null); setConnectionError(false); return undefined; }
    let cancelled = false;
    let timer;
    async function poll() {
      try {
        const current = await fetchTmyEstimationJob(projectId, accessToken);
        if (cancelled) return;
        setJob(current);
        setJobScope(scope);
        setConnectionError(false);
        if (["queued", "running"].includes(current?.status)) {
          setProgressScope(scope);
          timer = setTimeout(poll, 2500);
        } else if (current?.status === "completed") {
          const estimate = await fetchTmyEstimate(projectId, accessToken);
          if (!cancelled && estimate) callbacks.current.onComplete(estimate);
        } else if (current?.status === "failed") {
          callbacks.current.onError(current.error || "The estimate could not be run.");
        }
      } catch (error) {
        if (cancelled) return;
        if (error.status === 403) { setJob(null); return; }
        setConnectionError(true);
        timer = setTimeout(poll, 5000);
      }
    }
    void poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [enabled, projectId, accessToken, scope, revision]);
  const current = enabled && jobScope === scope ? job : null;
  return {
    job: current,
    pending: ["queued", "running"].includes(current?.status),
    showProgress: enabled && jobScope === scope && progressScope === scope,
    connectionError: enabled && connectionError,
    submitted: (value) => {
      setJobScope(scope); setProgressScope(scope); setJob(value); setConnectionError(false);
      setRevision((value) => value + 1);
    },
  };
}
